import {
  DATE_FORMAT,
  DATE_FORMAT_RU,
  DATE_TIME_FORMAT,
  DATE_TIME_FORMAT_RU,
  DATE_TIME_FORMAT_WITH_SEC,
  DATE_TIME_FORMAT_WITH_SEC_RU,
  MESSAGE_TIMESTAMP_FORMAT_OPTIONS,
  SHORT_DATE_FORMAT,
  SHORT_DATE_FORMAT_RU,
  TIME_FORMAT,
  TIME_FORMAT_RU,
} from "../constants/app";
import type {
  IFormatMessageTimestampProps,
  TDateFnsLocaleKey,
} from "./date.types";
import { toWesternDigits } from "./western-digits";

// NOTE: Converts a local Date to an end-of-day UTC ISO string to align
// client/server timezone semantics — the server validates against Date.now().
export const toEndOfDayUtcIsoString = (date: Date): string => {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999,
    ),
  ).toISOString();
};

// NOTE: A date-only key is a CALENDAR DAY, not an instant, and `new Date()`
// disagrees: it parses `2026-08-20` as UTC midnight, so every formatter that
// renders in local time prints the day BEFORE for anyone west of UTC. The
// analytics axis and its tooltip labelled a bar `Aug 19` that the server had
// keyed `2026-08-20`.
//
// The server has already resolved the day in the CLIENT's own offset before
// slicing the key (`formatDateWithClientOffset`), so the string is the user's
// calendar date and has to be rebuilt as local midnight rather than re-read as
// UTC. Shared, not per-app: web and mobile render the same key, and fixing one
// alone would put the two platforms a day apart.
//
// Anything that is not a bare `YYYY-MM-DD` is a real instant (a full ISO
// timestamp, say) and is left to `Date` to parse exactly as before.
const DATE_ONLY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const parseLocalDateKey = (value: string): Date =>
  DATE_ONLY_KEY_PATTERN.test(value)
    ? new Date(
        Number(value.slice(0, 4)),
        Number(value.slice(5, 7)) - 1,
        Number(value.slice(8, 10)),
      )
    : new Date(value);

export const getShortDateFormat = (locale: string): string =>
  locale === "ru" ? SHORT_DATE_FORMAT_RU : SHORT_DATE_FORMAT;

export const getDateFormat = (locale: string): string =>
  locale === "ru" ? DATE_FORMAT_RU : DATE_FORMAT;

export const getDateTimeFormat = (locale: string): string =>
  locale === "ru" ? DATE_TIME_FORMAT_RU : DATE_TIME_FORMAT;

export const getDateTimeFormatWithSec = (locale: string): string =>
  locale === "ru" ? DATE_TIME_FORMAT_WITH_SEC_RU : DATE_TIME_FORMAT_WITH_SEC;

export const getTimeFormat = (locale: string): string =>
  locale === "ru" ? TIME_FORMAT_RU : TIME_FORMAT;

// NOTE: Exact match on the language code, deliberately — `getDateFormat` above
// already matches that way, and a helper that resolved `ru-RU` while its
// neighbour did not would reintroduce the very disagreement this closes.
export const getDateFnsLocaleKey = (locale: string): TDateFnsLocaleKey => {
  if (locale === "ru") return "ru";
  if (locale === "ar") return "ar";
  return "enUS";
};

// NOTE: NSAI-5022 — the `Intl` tag per app language, keyed off the same
// `getDateFnsLocaleKey` mapping so a language cannot mean one locale to date-fns
// and another to `Intl`. No region is pinned for Arabic: `-u-nu-latn` only asks
// for Western digits, which the team chose over Arabic-Indic ones.
const INTL_LOCALE_BY_KEY: Record<TDateFnsLocaleKey, string> = {
  ar: "ar-u-nu-latn",
  enUS: "en-US",
  ru: "ru",
};

// NOTE: NSAI-4570 — `formatNumber` resolves the app language through this same
// table, so a number and the chat timestamp beside it cannot pick different
// locales for one language.
export const resolveIntlLocale = (language: string): string =>
  INTL_LOCALE_BY_KEY[getDateFnsLocaleKey(language)];

// NOTE: U+200E / U+200F / U+061C. `Intl` wraps Arabic date separators in
// right-to-left marks (`14\u200F/08\u200F/2026`). Inside the left-to-right
// timestamp row those marks turn each separator into an RTL island and the
// browser can paint the date back to front, so they are dropped.
const BIDI_MARK_PATTERN = /[\u200E\u200F\u061C]/g;

// NOTE: Newer ICU puts a narrow no-break space (U+202F) before `AM` / `PM`;
// older engines print a plain space. Normalising keeps the string identical on
// every browser and device.
const NARROW_NO_BREAK_SPACE_PATTERN = /\u202F/g;

// HACK: Some JS engines (older Hermes / ICU builds) ignore `-u-nu-latn` and
// still print Eastern digits, so they are mapped back through the same
// `toWesternDigits` fallback `formatNumber` uses. Exported for the token-usage
// row's device-locale timestamp, which carries the same marks under `ar-EG`.
export const normalizeIntlOutput = (value: string): string =>
  toWesternDigits(
    value
      .replace(BIDI_MARK_PATTERN, "")
      .replace(NARROW_NO_BREAK_SPACE_PATTERN, " "),
  );

// NOTE: The separator between the Arabic date and time is not stable across
// ICU versions — Node prints `15/06/2022، 02:30 PM`, Chromium
// `15/06/2022 02:30 PM`. It is pinned to the Arabic comma the team signed off
// on, so web and mobile cannot disagree.
const ARABIC_DATE_TIME_SEPARATOR = "، ";

const UTC_TIME_ZONE = "UTC";

const MILLISECONDS_PER_MINUTE = 60_000;

// NOTE: `getTimezoneOffset` reads the device's current zone on every call, so
// the shifted instant, printed in UTC, is the local wall-clock time even after
// the zone changes. Same local time the date-fns `format` this replaced showed.
const toWallClockInstant = (date: Date): Date =>
  new Date(date.getTime() - date.getTimezoneOffset() * MILLISECONDS_PER_MINUTE);

// NOTE: Building an `Intl.DateTimeFormat` is the expensive part, and a long
// chat formats one stamp per message on every render, so each formatter is
// built once per language and reused. They are pinned to UTC and fed a date
// already shifted to the device's wall clock (`toWallClockInstant`), so a
// cached formatter never holds on to the time zone it was built in: a device
// that changes zone mid-session gets the new local time on the next render.
const messageTimestampFormatters = new Map<
  TDateFnsLocaleKey,
  Intl.DateTimeFormat
>();

const getMessageTimestampFormatter = (
  localeKey: TDateFnsLocaleKey,
): Intl.DateTimeFormat => {
  const cached = messageTimestampFormatters.get(localeKey);
  if (cached) return cached;

  const formatter = new Intl.DateTimeFormat(INTL_LOCALE_BY_KEY[localeKey], {
    ...MESSAGE_TIMESTAMP_FORMAT_OPTIONS,
    timeZone: UTC_TIME_ZONE,
  });
  messageTimestampFormatters.set(localeKey, formatter);
  return formatter;
};

const ENGLISH_DAY_PERIOD_FORMATTER = new Intl.DateTimeFormat(
  INTL_LOCALE_BY_KEY.enUS,
  { hour: "2-digit", hour12: true, timeZone: UTC_TIME_ZONE },
);

const getEnglishDayPeriod = (date: Date): string | undefined =>
  ENGLISH_DAY_PERIOD_FORMATTER.formatToParts(date).find(
    (part) => part.type === "dayPeriod",
  )?.value;

// NOTE: NSAI-5022 — the timestamp under a chat bubble. Arabic used to fall
// through to the English `MMM d, yyyy hh:mm a` pattern; it now gets a numeric,
// day-first date like Russian, but keeps the English `AM` / `PM` marker the team
// asked for (`14/08/2026، 08:46 PM`), so its `dayPeriod` part is swapped for the
// English one.
//
// An invalid date renders as an empty string instead of throwing: `Intl` raises
// `RangeError` on one, and every caller formats an API timestamp in render.
export const formatMessageTimestamp = ({
  date,
  language,
}: IFormatMessageTimestampProps): string => {
  if (Number.isNaN(date.getTime())) return "";

  const localeKey = getDateFnsLocaleKey(language);
  const wallClockInstant = toWallClockInstant(date);
  const parts =
    getMessageTimestampFormatter(localeKey).formatToParts(wallClockInstant);
  const isArabic = localeKey === "ar";
  const englishDayPeriod = isArabic
    ? getEnglishDayPeriod(wallClockInstant)
    : undefined;
  const yearIndex = parts.findIndex((part) => part.type === "year");

  return normalizeIntlOutput(
    parts
      .map((part, index) => {
        // HACK: Hermes ignores `hour: "2-digit"` on a 12-hour clock and prints
        // `8:46 PM` where browsers print `08:46 PM`, so the hour is padded here
        // to keep mobile identical to web.
        if (part.type === "hour") return part.value.padStart(2, "0");
        if (!isArabic) return part.value;
        if (part.type === "dayPeriod" && englishDayPeriod) {
          return englishDayPeriod;
        }
        if (index === yearIndex + 1 && part.type === "literal") {
          return ARABIC_DATE_TIME_SEPARATOR;
        }
        return part.value;
      })
      .join(""),
  );
};

// NOTE: A calendar's weekday header is a fixed-width cell — 7 of them have to
// fit one screen, so it is sized for the two-character forms `en` and `ru` use
// (`Su`, `вс`). Arabic's short weekday names run to six characters
// (`أربعاء`), which on mobile hits the header's hardcoded `numberOfLines={1}`
// and renders as `رب...`; a truncated name is not a localised one.
//
// The rule is "widest form that fits", not "Arabic is special": a language
// whose short names exceed the cell falls back to the narrow form, which for
// Arabic is `ح ن ث ر خ ج س` — the abbreviation an Arabic calendar actually
// uses. `en` and `ru` are unaffected, so their headers stay byte-identical.
export const WEEKDAY_HEADER_MAX_LENGTH = 3;

export const shouldUseNarrowWeekdayNames = (shortNames: string[]): boolean =>
  shortNames.some((name) => name.length > WEEKDAY_HEADER_MAX_LENGTH);
